import React, { useCallback, useEffect, useMemo } from "react";

import { differenceInDays } from "date-fns";
import { uniqBy } from "lodash";
import { useEffectOnce, useSessionStorage } from "usehooks-ts";

import { usePageTitle } from "@/atoms/page-title-atom";
import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import { LoadingSlide, NoDataView } from "@components/title-slide-view";
import { useUser } from "@components/user-context";
import { LogOnMount } from "@components/v2/analytics";
import { BenchmarkSettings } from "@features/benchmarks";
import { AnnotationsFlyout } from "@features/custom-annotations";
import { useAnnotations } from "@features/custom-annotations/hooks/use-annotations";
import { usePrevious } from "@hooks/use-previous";
import { logEvent } from "@internal/analytics";
import {
  getSalesPageMetricSpecs,
  makeFirstTimeVsReturningRows,
  makeOverviewRows,
  makeTrafficRows,
} from "@north-beam/nb-client-side";
import { PLATFORM_NORTHBEAM } from "@north-beam/nb-common";
import { FlyoutSection } from "@pages/sales/flyout-section";
import {
  ReportBodyControl,
  useReportBodyState,
} from "@pages/sales/report-body-control";
import { SalesAPIErrorBoundary } from "@pages/sales/sales-page-error-container";
import { SalesViewsProvider } from "@pages/sales/sales-views-context";
import { useCustomSalesViews } from "@pages/sales/use-custom-sales-views";

import { useAdsetFilters, useCampaignFilters } from "./atoms";
import { ChartSection } from "./components/chart";
import { TabTableSection, TabView } from "./components/tab-table-section";
import { useSalesChartReportDataFilter } from "./hooks/use-sales-chart-filter";
import { useSalesChartReport } from "./hooks/use-sales-chart-report";
import { useSalesTableReportDataFilter } from "./hooks/use-sales-table-filter";
import { useSalesTableReport } from "./hooks/use-sales-table-report";
import { needToSwitchApisCalls } from "./utils";

export const MAX_DAYS = 35;

function FastSalesPage() {
  const [, setCampaignFilters] = useCampaignFilters();
  const [, setAdsetFilters] = useAdsetFilters();
  const [, setPageTitle] = usePageTitle();
  useEffect(() => {
    setPageTitle("Sales");
  }, [setPageTitle]);

  useEffect(() => {
    setCampaignFilters([]);
    setAdsetFilters([]);
  }, [setAdsetFilters, setCampaignFilters]);

  return <FastSalesPageContainer />;
}

function FastSalesPageContainer() {
  const { value: breakdownConfigs } = useSalesBreakdownConfigs();
  const { user } = useUser();
  const { isCustomSalesViewsLoading } = useCustomSalesViews();

  // Resets because it should not persist across refreshes
  const [, setCurrentSalesViewEdit] = useSessionStorage<null>(
    "currentSalesViewEdit",
    null,
  );
  useEffectOnce(() => {
    setCurrentSalesViewEdit(null);
  });

  const {
    annotations,
    fetchAnnotations,
    isAnnotationsLoading,
    createAnnotation,
    updateAnnotation,
    deleteAnnotation,
  } = useAnnotations();

  const {
    state,
    dateRange,
    compareDateRange,
    attributionModel,
    attributionWindow,
    nbAccountingMode,
    granularity,
    timeGranularity,

    setGranularity,
    setBreakdowns,
  } = useReportBodyState();

  const prevState = usePrevious(state);

  const selectPlatformBreakdown = useCallback(() => {
    const platform = breakdownConfigs.find(
      ({ key }) => key === PLATFORM_NORTHBEAM,
    );
    if (platform) {
      const platformOption = {
        key: platform.key,
        values: platform.choices.map(({ value }) => value),
      };
      setBreakdowns([platformOption]);
      setGranularity(TabView.PLATFORM);
    }
  }, [breakdownConfigs, setGranularity, setBreakdowns]);

  const unselectPlatformBreakdown = useCallback(() => {
    const breakdowns = state.breakdowns.filter(
      ({ key }) => key !== PLATFORM_NORTHBEAM,
    );
    setBreakdowns(breakdowns);
  }, [state.breakdowns, setBreakdowns]);

  const {
    loadAllData: loadAllTableData,
    data: salesReport,
    isLoading: tableDataIsLoading,
    error: tableError,
    refetch: tableRefetch,
    loadOnlyAdsetsData,
    loadOnlyCampaignData,
    loadOnlyAdData,
  } = useSalesTableReport(granularity, {
    dateRange,
    compareDateRange,
    attributionModel,
    attributionWindow,
    nbAccountingMode,
    timeGranularity,
  });
  const {
    loadAllData: loadAllChartData,
    data: chartData,
    isLoading: chartDataIsLoading,
    error: chartError,
    refetch: chartRefetch,
    loadOnlyAdsetsData: loadOnlyChartAdsetsData,
    loadOnlyCampaignData: loadOnlyChartCampaignData,
    loadOnlyAdData: loadOnlyChartAdData,
  } = useSalesChartReport(granularity, {
    dateRange,
    compareDateRange,
    attributionModel,
    attributionWindow,
    nbAccountingMode,
    timeGranularity,
  });

  const salesChartReport = chartData?.me?.salesReportChart;

  const metricsArray = useMemo(() => {
    const metrics = [
      ...makeOverviewRows(
        attributionModel,
        attributionWindow,
        salesReport?.hasV1Forecasts ?? false,
        nbAccountingMode,
        salesReport?.customGoals ?? [],
        timeGranularity,
        user,
      ),
      ...makeTrafficRows(
        attributionModel,
        attributionWindow,
        nbAccountingMode,
        user,
        timeGranularity,
      ),
      ...makeFirstTimeVsReturningRows(
        attributionModel,
        attributionWindow,
        nbAccountingMode,
        timeGranularity,
        user.attributionMethodEnum,
      ),
    ].filter((metric) => {
      const isEnabled = metric.pagesEnabled?.salesV2;

      // Only hide if explicitly disabled
      if (isEnabled === false) {
        return false;
      }

      return true;
    });

    return uniqBy(metrics, "id");
  }, [
    salesReport,
    user,
    attributionModel,
    attributionWindow,
    nbAccountingMode,
    timeGranularity,
  ]);

  const metricSpecs = useMemo(() => {
    return getSalesPageMetricSpecs(
      user,
      salesReport?.hasV1Forecasts ?? false,
      salesReport?.customGoals ?? [],
      [],
    );
  }, [salesReport, user]);

  const {
    filteredRows,
    clearLevelFilter: clearTableLevelFilter,
    setObjectIdsToAdLevelFilters: setTableObjectIdsToAdLevelFilters,
    salesTabTableSelectedCount,
  } = useSalesTableReportDataFilter(salesReport, granularity as TabView);

  const {
    filteredRows: chartFilteredRows,
    clearLevelFilter: clearChartLevelFilter,
    setObjectIdsToAdLevelFilters: setChartObjectIdsToAdLevelFilters,
  } = useSalesChartReportDataFilter(salesChartReport, granularity as TabView);

  const clearLevelFilter = useCallback(
    (level: TabView) => {
      clearTableLevelFilter(level);
      clearChartLevelFilter(level);
    },
    [clearChartLevelFilter, clearTableLevelFilter],
  );

  const setObjectIdsToAdLevelFilters = useCallback(
    (selectedRows: any, level: TabView) => {
      setTableObjectIdsToAdLevelFilters(selectedRows, level);
      setChartObjectIdsToAdLevelFilters(selectedRows, level);
    },
    [setChartObjectIdsToAdLevelFilters, setTableObjectIdsToAdLevelFilters],
  );

  useEffect(() => {
    logEvent("Request Sales Report V2", state);
    if (!needToSwitchApisCalls(state, prevState)) {
      return;
    }

    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);

    if (differenceInDays(endDate, startDate) <= MAX_DAYS) {
      loadAllTableData();
      loadAllChartData();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateRange.startDate,
    dateRange.endDate,

    state.compareDateRange,
    state.attributionModel,
    state.attributionWindow,
    state.nbAccountingMode,
    state.timeGranularity,
  ]);

  useEffect(() => {
    if (!needToSwitchApisCalls(state, prevState)) {
      return;
    }

    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);

    // if there is more than 30 days you wanna pull, do it individually
    if (differenceInDays(endDate, startDate) > MAX_DAYS) {
      switch (state.granularity) {
        case "ad":
          loadOnlyAdData();
          loadOnlyChartAdData();
          break;
        case "adset":
          loadOnlyAdsetsData();
          loadOnlyChartAdsetsData();
          break;
        case "campaign":
        case "platform":
          loadOnlyCampaignData();
          loadOnlyChartCampaignData();
          break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateRange.startDate,
    dateRange.endDate,
    state.granularity,

    state.compareDateRange,
    state.attributionModel,
    state.attributionWindow,
    state.nbAccountingMode,
    state.timeGranularity,
  ]);

  if (tableError || chartError) {
    return (
      <SalesAPIErrorBoundary
        refetch={() => {
          chartRefetch?.();
          tableRefetch?.();
        }}
      />
    );
  }

  const isLoadingData = tableDataIsLoading;
  if (
    isCustomSalesViewsLoading ||
    isLoadingData ||
    (breakdownConfigs.length === 1 && breakdownConfigs[0].default)
  ) {
    return <LoadingSlide />;
  }

  if (!salesReport || salesReport.rows.length === 0) {
    return <NoDataView />;
  }

  return (
    <SalesViewsProvider>
      <LogOnMount name="Visit Sales page" />
      <ReportBodyControl
        isLoading={isLoadingData}
        setObjectIdsToAdLevelFilters={setObjectIdsToAdLevelFilters}
      />
      <div className="px-4 pb-16 flex flex-col space-y-6">
        <AnnotationsFlyout
          annotations={annotations}
          fetchAnnotations={fetchAnnotations}
          isAnnotationsLoading={isAnnotationsLoading}
          createAnnotation={createAnnotation}
          updateAnnotation={updateAnnotation}
          deleteAnnotation={deleteAnnotation}
        />
        <FlyoutSection state={state} rows={filteredRows} />
        <ChartSection
          rows={chartFilteredRows}
          metricsArray={metricsArray}
          isLoading={chartDataIsLoading}
        />
        {user.featureFlags.showBenchmarks && <BenchmarkSettings />}
        <TabTableSection
          rows={filteredRows}
          salesTabTableSelectedCount={salesTabTableSelectedCount}
          clearLevelFilter={clearLevelFilter}
          metricsArray={metricsArray}
          selectPlatformBreakdown={selectPlatformBreakdown}
          unselectPlatformBreakdown={unselectPlatformBreakdown}
          setObjectIdsToAdLevelFilters={setObjectIdsToAdLevelFilters}
          salesPageMetricSpecs={metricSpecs}
        />
      </div>
    </SalesViewsProvider>
  );
}

export default FastSalesPage;
